import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, BadgeCheck, MapPin, ShieldCheck } from "lucide-react";
import Navbar from "../../../components/Navbar/Navbar";
import Footer from "../../../components/Footer/Footer";
import PropertyCard from "../../../components/PropertyCard/PropertyCard";
import Loader from "../../../components/Loader/Loader";
import { getProperties, getCities } from "../../../services/api";
import cityimages from "../../../assets/images/real-estate1.jpg";
import "./City.css";

function isPublicListing(p) {
  const status = String(p.status || "").trim().toLowerCase();
  const published = !status || status === "published";
  const active = p.isActive !== false && p.isActive !== "false";
  return published && active;
}

function cityKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
}

function matchesCity(p, citySlug, cityRecord) {
  const slug = cityKey(citySlug);
  const pCity = cityKey(p.city);

  if (pCity === slug) return true;

  if (cityRecord) {
    const name = cityKey(cityRecord.name);
    const cSlug = cityKey(cityRecord.slug || cityRecord.name);
    if (pCity === name || pCity === cSlug) return true;
  }

  return pCity.includes(slug) || slug.includes(pCity);
}

export default function City() {
  const { city: citySlug } = useParams();
  const [items, setItems] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [loading, setLoading] = useState(true);

  const city = useMemo(() => {
    const slug = cityKey(citySlug);
    return (
      cityList.find(
        (c) =>
          cityKey(c.slug) === slug ||
          cityKey(c.name) === slug ||
          cityKey(c.name).replace(/\s+/g, "") === slug.replace(/-/g, "")
      ) || null
    );
  }, [cityList, citySlug]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const [properties, cities] = await Promise.all([
          getProperties(),
          getCities().catch(() => []),
        ]);

        if (cancelled) return;

        const list = Array.isArray(cities) ? cities : [];
        setCityList(list);

        const cityRecord =
          list.find(
            (c) =>
              cityKey(c.slug) === cityKey(citySlug) ||
              cityKey(c.name) === cityKey(citySlug)
          ) || null;

        const filtered = (Array.isArray(properties) ? properties : [])
          .filter(isPublicListing)
          .filter((p) => matchesCity(p, citySlug, cityRecord));

        setItems(filtered);
      } catch (err) {
        console.error("City page load failed:", err);
        if (!cancelled) {
          setItems([]);
          setCityList([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [citySlug]);

  const displayName = city?.name ?? citySlug ?? "…";
  const linkSlug = (c) =>
    c.slug || cityKey(c.name) || c.id;

  return (
    <div className="city-page">
      <Navbar />

      <section className="ct-hero">
        <div className="ct-hero-bg">
          <img
            src={cityimages}
            alt=""
            width={1920}
            height={1080}
            loading="eager"
          />
        </div>

        <div className="ct-hero-inner">
          <div>
            <span className="ct-kicker">City Guide</span>

            <h1 className="ct-title">
              Premium living in
              <span>{displayName}</span>
            </h1>

            <p className="ct-sub">
              Handpicked, RERA-verified projects across {displayName} — from
              signature high-rises to gated communities and investment-grade
              plots, curated by our advisory team.
            </p>

            <div className="ct-meta">
              <span className="ct-pill">
                <MapPin className="h-3.5 w-3.5" />
                {loading
                  ? "Loading projects"
                  : `${items.length} live projects`}
              </span>
              <span className="ct-pill">
                <ShieldCheck className="h-3.5 w-3.5" /> RERA verified
              </span>
              <span className="ct-pill">
                <BadgeCheck className="h-3.5 w-3.5" /> Zero brokerage
              </span>
            </div>
          </div>

          <div className="ct-switch">
            <div className="ct-switch-label">Switch city</div>
            <div className="ct-switch-grid">
              {cityList.map((c) => {
                const slug = linkSlug(c);
                const active =
                  cityKey(slug) === cityKey(citySlug) ||
                  cityKey(c.name) === cityKey(citySlug);

                return (
                  <Link
                    key={c.id || c.name}
                    to={`/city/${slug}`}
                    className={`ct-city ${active ? "is-active" : ""}`}
                  >
                    {c.name}
                    <ArrowUpRight className="arrow h-3.5 w-3.5" />
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        <div className="ct-hero-fade" />
      </section>

      <section className="ct-body">
        <div className="ct-container">
          {loading && <Loader label="Loading properties…" />}

          {!loading && items.length === 0 && (
            <div className="ct-empty">No properties yet in this city.</div>
          )}

          {!loading && items.length > 0 && (
            <div className="ct-grid">
              {items.map((p) => (
                <PropertyCard key={p.id} property={p} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}